import { useId, useState } from 'react';
import { IconButton } from '@eventbrite/marmalade';
import Image from 'next/image';
import { defineMessages, useIntl } from 'react-intl';

import { withBasePath } from '@/utils/withBasePath';

import styles from './FeaturedEvent.module.scss';

const messages = defineMessages({
  pin: {
    id: 'Pin {eventName} as the highlighted event',
    defaultMessage: 'Pin {eventName} as the highlighted event',
  },
  unpin: {
    id: 'Remove {eventName} from highlighted events',
    defaultMessage: 'Remove {eventName} from highlighted events',
  },
  tooltipTitle: {
    id: 'Pin this event',
    defaultMessage: 'Pin this event',
  },
  tooltipDescription: {
    id: "It'll appear first and highlighted on your profile. You can pin one event at a time.",
    defaultMessage:
      "It'll appear first and highlighted on your profile. You can pin one event at a time.",
  },
});

interface Props {
  eventName: string;
  isPinned: boolean;
  showTooltip?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export function FeaturedEventPinButton({
  eventName,
  isPinned,
  showTooltip = false,
  disabled,
  onClick,
}: Props) {
  const intl = useIntl();
  const tooltipId = useId();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const label = intl.formatMessage(isPinned ? messages.unpin : messages.pin, { eventName });
  const shouldRenderTooltip = showTooltip && !isPinned && isTooltipOpen;

  return (
    <span
      className={styles.pinTooltipAnchor}
      onMouseEnter={() => setIsTooltipOpen(true)}
      onMouseLeave={() => setIsTooltipOpen(false)}
      onFocusCapture={() => setIsTooltipOpen(true)}
      onBlurCapture={() => setIsTooltipOpen(false)}
    >
      <IconButton
        icon={
          <Image
            src={withBasePath(`/images/action-${isPinned ? 'unpin' : 'pin'}-filled.svg`)}
            alt=""
            width={24}
            height={24}
            aria-hidden
            unoptimized
          />
        }
        outline
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsTooltipOpen(false);
          onClick();
        }}
        aria-label={label}
        aria-describedby={shouldRenderTooltip ? tooltipId : undefined}
        aria-pressed={isPinned}
        className={isPinned ? styles.pinnedButton : styles.pinButton}
        data-testid={`featured-event-pin-${isPinned ? 'active' : 'inactive'}`}
      />
      {shouldRenderTooltip ? (
        <span id={tooltipId} role="tooltip" className={styles.pinTooltip}>
          <Image
            src={withBasePath('/images/featured-event-tooltip-preview.png')}
            alt=""
            width={256}
            height={128}
            aria-hidden
            className={styles.pinTooltipPreview}
            unoptimized
          />
          <span className={styles.pinTooltipCopy}>
            <span className={styles.pinTooltipTitle}>
              {intl.formatMessage(messages.tooltipTitle)}
            </span>
            <span className={styles.pinTooltipDescription}>
              {intl.formatMessage(messages.tooltipDescription)}
            </span>
          </span>
        </span>
      ) : null}
    </span>
  );
}
